import { useEffect } from "react";

import { COLORS } from "utils/colors";
import "./Compass.css";

/**
 * Compass component renders a circular radar-style compass with grid lines, axes, and rotating indicators.
 *
 * @memberof module:components/compass
 * @param {object} props - Component props.
 * @param {string} [props.color] - Optional color override (not currently used).
 * @param {number|string} [props.size] - Optional size override (not currently used).
 * @param {number[]} [props.degree] - Array of degree values to place dots on the compass circle.
 * @param {...object} otherProps - Additional SVG props to spread on the root svg element.
 *
 * @returns {JSX.Element} SVG compass visualization.
 */
const Compass = ({ color, size, degree, ...otherProps }) => {
  const bluePalette = {
    background: COLORS.ligthGrey, // Dark blue for background
    grids: "#34556e", // A slightly lighter blue for grids
    axis: COLORS.selectedColorTheme, // A bright blue for axes
    target: COLORS.selectedColorTheme, // A light blue for the target or special elements
  };

  useEffect(() => {
    const radarIndicator = document.getElementById("radar_indicator");
    const circles = document.getElementById("g.locations > circle");

    const checkIntersection = () => {
      circles?.forEach((circle) => {
        const circleRect = circle?.getBoundingClientRect();
        const radarRect = radarIndicator.getBoundingClientRect();

        if (
          circleRect.left < radarRect.right &&
          circleRect.right > radarRect.left &&
          circleRect.top < radarRect.bottom &&
          circleRect.bottom > radarRect.top
        ) {
          circle.style.opacity = 0.3; // Change opacity when intersecting
        } else {
          circle.style.opacity = 1; // Reset opacity when not intersecting
        }
      });
    };

    setInterval(checkIntersection, 100); // Check intersection every 100 milliseconds

    return () => clearInterval(checkIntersection);
  }, []);
  const pallete = bluePalette;

  /**
   * Rotate a point (x, y) around center (cx, cy) by given angle in degrees.
   *
   * @param {number} cx - X coordinate of center point.
   * @param {number} cy - Y coordinate of center point.
   * @param {number} x - X coordinate of the point to rotate.
   * @param {number} y - Y coordinate of the point to rotate.
   * @param {number} angle - Angle in degrees to rotate.
   * @returns {{x: number, y: number}} Rotated point coordinates.
   */
  function rotatePoint(cx, cy, x, y, angle) {
    let radians = angle * (Math.PI / 180);
    let translatedX = x - cx;
    let translatedY = y - cy;
    let rotatedX =
      translatedX * Math.cos(radians) - translatedY * Math.sin(radians);
    let rotatedY =
      translatedX * Math.sin(radians) + translatedY * Math.cos(radians);
    let finalX = rotatedX + cx;
    let finalY = rotatedY + cy;
    return { x: finalX, y: finalY };
  }

  return (
    <>
      <svg
        //version="1.0"
        xmlns="http://www.w3.org/2000/svg"
        width="512.000000pt"
        height="512.000000pt"
        viewBox="-1 -1 101 101"
        preserveAspectRatio="xMidYMid meet"
        {...otherProps}
      >
        <defs>
          <clipPath id="circleClip">
            <circle cx="50" cy="50" r="40" />
          </clipPath>

          <linearGradient id="grad" gradientTransform="rotate(90)">
            <stop offset="0%" stopColor="#000" />
            <stop offset="50%" stopColor="#fff" />
          </linearGradient>
          <linearGradient id="grad2" gradientTransform="rotate(85)">
            <stop offset="0%" stopColor="rgba(23, 125, 220, 1)" />
            <stop offset="20%" stopColor="rgba(23, 125, 220 ,0)" />
          </linearGradient>
          <radialGradient
            id="grad3"
            cx="50%"
            cy="50%"
            r="55%"
            fx="50%"
            fy="50%"
          >
            <stop
              offset="0%"
              stopColor="rgba(255,255,255, 0.7)"
              stopOpacity="0"
            />
            <stop offset="100%" stopColor="rgba(0,0,0,0.7)" stopOpacity="1" />
          </radialGradient>
          <filter id="f1" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="0.3" />
          </filter>
          <mask id="bby">
            <path
              d="M 10 50 A 1 1 0 0 0 90 50 Z"
              mask="url(#bby)"
              fill="url(#grad)"
            >
              <animateTransform
                attributeName="transform"
                attributeType="XML"
                type="rotate"
                from="0 50 50"
                to="360 50 50"
                dur="7s"
                repeatCount="indefinite"
              />
            </path>
          </mask>
        </defs>

        <circle fill={pallete.background} cx="256" cy="256" r="206" />

        {/* First grid */}
        <g
          stroke={pallete.grids}
          strokeWidth="1"
          opacity="0.5"
          clipPath="url(#circleClip)"
        >
          {/* Vertical lines */}
          {Array.from({ length: 10 }).map((_, index) => (
            <line
              key={`vertical-1-${index}`}
              x1={10 * index}
              y1="0"
              x2={10 * index}
              y2="100"
            />
          ))}

          {/* Horizontal lines */}
          {Array.from({ length: 10 }).map((_, index) => (
            <line
              key={`horizontal-1-${index}`}
              x1="0"
              y1={10 * index}
              x2="100"
              y2={10 * index}
            />
          ))}
        </g>
        <g>
          <circle
            stroke={pallete.axis}
            strokeWidth="1"
            fill="none"
            cx="50"
            cy="50"
            r="40"
          />

          <circle
            stroke={pallete.axis}
            strokeWidth="1"
            fill="none"
            cx="50"
            cy="50"
            r="30"
          />

          <circle
            stroke={pallete.axis}
            strokeWidth="1"
            fill="none"
            cx="50"
            cy="50"
            r="20"
          />

          <circle
            stroke={pallete.axis}
            strokeWidth="1"
            fill="none"
            cx="50"
            cy="50"
            r="10"
          />

          <circle
            stroke={pallete.axis}
            strokeWidth="1"
            fill="none"
            cx="50"
            cy="50"
            r="1"
          />

          <line
            x1="50"
            y1="0"
            x2="50"
            y2="100"
            stroke={pallete.axis}
            strokeWidth="1"
            clipPath="url(#circleClip)"
          />

          <line
            x1="0"
            y1="50"
            x2="100"
            y2="50"
            stroke={pallete.axis}
            strokeWidth="1"
            clipPath="url(#circleClip)"
          />
        </g>
        <path d="M 50 50 L 50 90 A 40 40 0 0 1 10 50 Z" fill="url(#grad2)">
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            from="0 50 50"
            to="360 50 50"
            dur="7s"
            begin="-6.8s"
            repeatCount="indefinite"
          />
        </path>

        <g className="dots" filter="url(#f1)" fill="rgba(191, 31, 39, 1)">
          {degree?.map((deg, index) => (
            <circle
              mask="url(#bby)"
              cx={rotatePoint(50, 50, 50, 25, deg).x}
              cy={rotatePoint(50, 50, 50, 25, deg).y}
              r="2"
            ></circle>
          ))}
        </g>
        <text
          x="50"
          y="8" // Adjust the y-coordinate based on your preference
          textAnchor="middle"
          fill={pallete.axis}
          fontSize="10"
          fontWeight="bold"
        >
          N
        </text>

        <text
          x="50"
          y="100" // Adjust the y-coordinate based on your preference
          textAnchor="middle"
          fill={pallete.axis}
          fontSize="10"
          fontWeight="bold"
        >
          S
        </text>

        <text
          x="94"
          y="53" // Adjust the y-coordinate based on your preference
          textAnchor="middle"
          fill={pallete.axis}
          fontSize="10"
          fontWeight="bold"
        >
          E
        </text>
        <text
          x="4"
          y="53" // Adjust the y-coordinate based on your preference
          textAnchor="middle"
          fill={pallete.axis}
          fontSize="10"
          fontWeight="bold"
        >
          W
        </text>
      </svg>
    </>
  );
};

export default Compass;